import React from 'react';
import { Button, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LockIcon from '@mui/icons-material/Lock';
import { AreaOccupancySlotData } from '../../../api';
import { AreaSeverity, HorizonResult } from '../utils/occupancyMath';
import { AREAS_PALETTE, severityColor } from '../areasPalette';
import { AreaUnitWords, horizonText } from '../utils/areasFormat';
import { FONT_SIZE } from '../agendaStyles';

type AreaPillarHeaderProps = {
  areaLabel: string;
  swatchColor: string;
  slotData: AreaOccupancySlotData | undefined;
  severity: AreaSeverity;
  horizon: HorizonResult;
  unitWords: AreaUnitWords;
  sessionMinutes: number;
  timeFormat: string;
  canManageBlocks: boolean;
  onManageBlocks: () => void;
};

/**
 * The 10-second rule lives here: area, how many are free right now, and whether a session fits —
 * readable without scrolling or expanding anything.
 */
export const AreaPillarHeader = ({
  areaLabel,
  swatchColor,
  slotData,
  severity,
  horizon,
  unitWords,
  sessionMinutes,
  timeFormat,
  canManageBlocks,
  onManageBlocks,
}: AreaPillarHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // The horizon sentence is long; on a phone it needs the width of the row to itself.
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isFullyBlocked = severity === AreaSeverity.BLOCKED_ALL;
  const blockedCount = slotData?.blocked ?? 0;

  const horizonLine = (
    <Typography sx={{ fontSize: FONT_SIZE.detail, color: 'text.secondary' }}>
      {horizonText({ result: horizon, unitWords, sessionMinutes, timeFormat, t })}
    </Typography>
  );

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="stretch"
      gap={0.5}
      wrap="nowrap"
      sx={{ padding: '10px 12px', minHeight: '56px' }}>
      <Grid container alignItems="center" gap={1.5} wrap="nowrap">
        <Grid
          container
          alignItems="center"
          gap={1}
          wrap="nowrap"
          sx={{ width: 'auto', minWidth: isSmallScreen ? 0 : '140px' }}>
          <Grid
            sx={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: swatchColor, flexShrink: 0 }}
          />
          <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{areaLabel}</Typography>
        </Grid>

        <Grid
          container
          alignItems="baseline"
          gap={0.5}
          wrap="nowrap"
          sx={{ width: 'auto', minWidth: isSmallScreen ? 0 : '90px' }}>
          {isFullyBlocked ? (
            <>
              <LockIcon sx={{ fontSize: '18px', color: AREAS_PALETTE.blocked }} />
              <Typography sx={{ fontWeight: 700, color: AREAS_PALETTE.blocked }}>
                {t('calendar.areas_view.closed')}
              </Typography>
            </>
          ) : (
            <>
              <Typography dir="ltr" sx={{ fontWeight: 700, fontSize: '18px', color: severityColor(severity) }}>
                {slotData ? `${slotData.free}/${slotData.total}` : '—'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{unitWords.statLabel}</Typography>
            </>
          )}
        </Grid>

        <Grid container alignItems="center" gap={1} wrap="wrap" sx={{ flex: 1 }}>
          {!isSmallScreen && horizonLine}

          {!isFullyBlocked && blockedCount > 0 && (
            <Chip
              size="small"
              icon={<LockIcon sx={{ fontSize: '14px' }} />}
              label={t('calendar.areas_view.blocked_chip', { count: blockedCount })}
              sx={{ backgroundColor: 'mb.lineSoft', height: '22px' }}
            />
          )}
        </Grid>

        {canManageBlocks && (
          <Button
            size="small"
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              onManageBlocks();
            }}
            sx={{ whiteSpace: 'nowrap' }}>
            {t('calendar.areas_view.close_area')}
          </Button>
        )}
      </Grid>

      {isSmallScreen && horizonLine}
    </Grid>
  );
};
